import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import posthog from 'posthog-js';
import { LightBulbIcon, MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import useApi from '../../hooks/useApi';
import useFixedViewportWidth from '../useFixedViewPortWidth';
import ThemeDropdownWithPreview from '../themesDropdown';
import {
  AUTHORED_EDITOR_EXTENSION_SET_VERSION,
  getAuthoredEditorExtensions,
} from '../../authored/tiptapExtensions';
import {
  ENHANCED_PAGE_BASE,
  PITCH_BLACK_THEME_ID,
  READING_LAMP_STORAGE_KEY,
  READER_FONT_BASE_PX,
  READER_FONT_SCALE_MAX,
  READER_FONT_SCALE_MIN,
  READER_FONT_STORAGE_KEY,
} from '../../reader/readerConstants';
import { getPageThemeChrome } from '../../reader/pageThemeChrome';
import AuthoredReaderSpreadLeaf from './AuthoredReaderSpreadLeaf';
import useReaderAffiliatePromo from '../../hooks/useReaderAffiliatePromo';

const INNER_MAIN_CLASS =
  'absolute inset-0 flex min-h-0 min-w-0 w-full flex-col items-stretch justify-start overflow-y-auto p-8 font-serif leading-relaxed text-gray-800 text-shadow whitespace-pre-line';
const INNER_STACK_CLASS =
  'absolute inset-0 flex min-h-0 min-w-0 w-full flex-col items-stretch justify-start overflow-hidden p-8 font-serif leading-relaxed text-gray-800 text-shadow';

const FALLBACK_PAGE_THEME = {
  theme_meta_unique_id: 'fallback-page',
  theme_name: 'Default',
  theme_url: '/images/clear-white-plaster-texture-pattern.jpg',
  is_available: 1,
};

const FALLBACK_BG_THEME = {
  theme_meta_unique_id: 'fallback-bg',
  theme_name: 'Default',
  theme_url: '/images/wood.jpg',
  has_audio: 0,
  is_available: 1,
};

/** Must be >= CSS flip animation duration (`index.css` flipNext/flipPrev: 0.6s). */
const PAGE_FLIP_COMMIT_MS = 650;

export default function AuthoredBookTheme({ bookId, previewMode = false }) {
  const { request } = useApi();
  const { recordForwardFlip, promoElement } = useReaderAffiliatePromo('authored_reader');
  const [book, setBook] = useState(null);
  const [pages, setPages] = useState([]);
  const [spreadIndex, setSpreadIndex] = useState(0);
  const [targetSpreadIndex, setTargetSpreadIndex] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const userNavigatedRef = useRef(false);

  const [readerFontScale, setReaderFontScale] = useState(() => {
    try {
      const raw = localStorage.getItem(READER_FONT_STORAGE_KEY);
      if (raw == null || raw === '') return 1;
      const v = parseFloat(raw);
      if (Number.isNaN(v)) return 1;
      return Math.min(READER_FONT_SCALE_MAX, Math.max(READER_FONT_SCALE_MIN, v));
    } catch {
      return 1;
    }
  });

  const [readingLampOn, setReadingLampOn] = useState(() => {
    try {
      const raw = localStorage.getItem(READING_LAMP_STORAGE_KEY);
      return raw === '1' || raw === 'true';
    } catch {
      return false;
    }
  });

  const [pitchBlackRestoreTheme, setPitchBlackRestoreTheme] = useState(null);
  const [backgroundThemes, setBackgroundThemes] = useState([]);
  const [pageThemes, setPageThemes] = useState([]);
  const [selectedPageTheme, setSelectedPageTheme] = useState(null);
  const [selectedBackgroundTheme, setSelectedBackgroundTheme] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [loadingTheme, setLoadingTheme] = useState(null);
  const [previewTheme, setPreviewTheme] = useState(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [audioVolume, setAudioVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);

  const clampedReaderFontScale = useMemo(
    () => Math.min(READER_FONT_SCALE_MAX, Math.max(READER_FONT_SCALE_MIN, readerFontScale)),
    [readerFontScale],
  );

  const enhancedScaledPagePx = useMemo(
    () => Math.round(ENHANCED_PAGE_BASE * clampedReaderFontScale),
    [clampedReaderFontScale],
  );

  const viewportLockWidth = useMemo(
    () => enhancedScaledPagePx * 2 + 10,
    [enhancedScaledPagePx],
  );

  const enhancedPageFlipEnabled = useMemo(
    () => Math.abs(clampedReaderFontScale - 1) < 0.001,
    [clampedReaderFontScale],
  );

  useFixedViewportWidth(viewportLockWidth);

  const extensions = useMemo(
    () => getAuthoredEditorExtensions({ editable: false }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- version token bumps extension list
    [AUTHORED_EDITOR_EXTENSION_SET_VERSION],
  );

  const updateUserBookDetail = useCallback(
    async ({ theme_id, last_read_page } = {}) => {
      if (previewMode) return;
      if (!localStorage.getItem('access_token')) return;
      const payload = {};
      if (theme_id !== undefined) payload.theme_id = theme_id;
      if (last_read_page !== undefined) payload.last_read_page = last_read_page;
      if (Object.keys(payload).length === 0) return;
      try {
        await request({
          method: 'patch',
          url: `/api/v1/pdf_parser/my_book_view/${bookId}/`,
          withAuth: true,
          data: payload,
        });
      } catch (err) {
        console.error('authored my_book_view PATCH', err);
      }
    },
    [bookId, request, previewMode],
  );

  const maxSpreadIndex = useMemo(
    () => Math.max(0, Math.ceil(pages.length / 2) - 1),
    [pages.length],
  );

  useEffect(() => {
    if (spreadIndex <= maxSpreadIndex) return;
    const next = maxSpreadIndex;
    setSpreadIndex(next);
    setTargetSpreadIndex(next);
  }, [maxSpreadIndex, spreadIndex]);

  useEffect(() => {
    if (targetSpreadIndex === spreadIndex) return undefined;
    const timer = setTimeout(() => {
      setSpreadIndex(targetSpreadIndex);
      void updateUserBookDetail({ last_read_page: targetSpreadIndex });
    }, PAGE_FLIP_COMMIT_MS);
    return () => clearTimeout(timer);
  }, [targetSpreadIndex, spreadIndex, updateUserBookDetail]);

  useEffect(() => {
    localStorage.setItem(READER_FONT_STORAGE_KEY, String(clampedReaderFontScale));
  }, [clampedReaderFontScale]);

  useEffect(() => {
    localStorage.setItem(READING_LAMP_STORAGE_KEY, readingLampOn ? '1' : '0');
  }, [readingLampOn]);

  const load = useCallback(async (isActive = () => true) => {
    setInitialLoading(true);
    const token = localStorage.getItem('access_token');
    let userId = 'guest';
    if (token) {
      userId = localStorage.getItem('user_id') || 'guest';
    }

    try {
      let b;
      let mode = 'public';
      try {
        if (token) {
          try {
            const authorRes = await request({
              method: 'get',
              url: `/api/v1/pdf_parser/author/books/${bookId}/`,
              withAuth: true,
            });
            b = authorRes.book;
            mode = 'author';
          } catch {
            const pubRes = await request({
              method: 'get',
              url: `/api/v1/pdf_parser/authored-books/${bookId}/`,
              withAuth: false,
            });
            b = pubRes.book;
          }
        } else {
          const pubRes = await request({
            method: 'get',
            url: `/api/v1/pdf_parser/authored-books/${bookId}/`,
            withAuth: false,
          });
          b = pubRes.book;
        }
      } catch {
        setBook(null);
        setPages([]);
        return;
      }
      setBook(b);

      const pagesUrl =
        mode === 'author'
          ? `/api/v1/pdf_parser/author/books/${bookId}/pages/`
          : `/api/v1/pdf_parser/authored-books/${bookId}/pages/`;

      const pdata = await request({
        method: 'get',
        url: pagesUrl,
        params: { start: 0, end: 199 },
        withAuth: mode === 'author',
      });
      const pageList = pdata.pages || [];
      setPages(pageList);

      let bt = [];
      let pt = [];
      try {
        const themeData = await request({
          method: 'get',
          url: `/api/v1/pdf_parser/themes/${userId}/`,
        });
        bt = themeData?.book_themes || [];
        pt = themeData?.page_themes || [];
        setBackgroundThemes(bt);
        setPageThemes(pt);
        setSelectedPageTheme(pt[0] || FALLBACK_PAGE_THEME);
        setSelectedBackgroundTheme(bt[0] || FALLBACK_BG_THEME);
      } catch {
        setBackgroundThemes([]);
        setPageThemes([]);
        setSelectedPageTheme(FALLBACK_PAGE_THEME);
        setSelectedBackgroundTheme(FALLBACK_BG_THEME);
      }

      const maxSp = Math.max(0, Math.ceil(pageList.length / 2) - 1);
      if (token && !previewMode) {
        try {
          const userData = await request({
            method: 'get',
            url: `/api/v1/pdf_parser/my_book_view/${bookId}/`,
            withAuth: true,
          });
          const bd = userData?.book_detail;
          if (bd) {
            const rawLp = bd.last_read_page;
            const parsedLp =
              rawLp != null && rawLp !== '' ? parseInt(String(rawLp), 10) : 0;
            const safeLp = Number.isNaN(parsedLp) ? 0 : Math.min(maxSp, Math.max(0, parsedLp));
            if (isActive() && !userNavigatedRef.current) {
              setSpreadIndex(safeLp);
              setTargetSpreadIndex(safeLp);
            }

            const tid =
              typeof bd.theme_id === 'object' && bd.theme_id !== null
                ? bd.theme_id.theme_meta_unique_id || bd.theme_id.theme_id
                : bd.theme_id;
            if (tid && bt.length) {
              const matched = bt.find((t) => t.theme_meta_unique_id === tid);
              if (matched) setSelectedBackgroundTheme(matched);
            }
          }
        } catch {
          /* no row yet or not in library — start at spread 0 */
        }
      }
    } finally {
      if (isActive()) {
        setInitialLoading(false);
      }
    }
  }, [bookId, request, previewMode]);

  useEffect(() => {
    userNavigatedRef.current = false;
    let active = true;
    void load(() => active);
    return () => {
      active = false;
    };
  }, [load]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.volume = audioVolume;
    el.muted = isMuted;
  }, [audioVolume, isMuted, selectedBackgroundTheme]);

  const handlePageThemeChange = useCallback((theme) => {
    if (!theme) return;
    setLoadingTheme(theme);
    const img = new Image();
    img.src = theme.theme_url;
    img.onload = () => {
      setSelectedPageTheme(theme);
      setLoadingTheme(null);
    };
    img.onerror = () => {
      setLoadingTheme(null);
    };
    if (!previewMode) {
      posthog.capture('authored_change_page_theme', {
        book_id: bookId,
        theme_name: theme.theme_name,
        theme_id: theme.theme_meta_unique_id,
      });
    }
  }, [bookId, previewMode]);

  const handleBackgroundThemeChange = useCallback(
    (theme) => {
      if (!theme) return;
      setLoadingTheme(theme);
      const noImage =
        !theme.theme_url ||
        String(theme.theme_url).trim() === '' ||
        theme.theme_meta_unique_id === PITCH_BLACK_THEME_ID;
      if (noImage) {
        setSelectedBackgroundTheme(theme);
        setLoadingTheme(null);
        void updateUserBookDetail({ theme_id: theme.theme_meta_unique_id });
        if (!previewMode) {
          posthog.capture('authored_change_background_theme', {
            book_id: bookId,
            theme_name: theme.theme_name,
            theme_id: theme.theme_meta_unique_id,
          });
        }
        return;
      }
      const img = new Image();
      img.src = theme.theme_url;
      img.onload = () => {
        setSelectedBackgroundTheme(theme);
        setLoadingTheme(null);
        void updateUserBookDetail({ theme_id: theme.theme_meta_unique_id });
      };
      img.onerror = () => {
        setLoadingTheme(null);
      };
      if (!previewMode) {
        posthog.capture('authored_change_background_theme', {
          book_id: bookId,
          theme_name: theme.theme_name,
          theme_id: theme.theme_meta_unique_id,
        });
      }
    },
    [bookId, updateUserBookDetail, previewMode],
  );

  const backgroundThemesForDropdown = useMemo(
    () => backgroundThemes.filter((t) => t.theme_meta_unique_id !== PITCH_BLACK_THEME_ID),
    [backgroundThemes],
  );

  const pitchBlackThemeMeta = useMemo(
    () => backgroundThemes.find((t) => t.theme_meta_unique_id === PITCH_BLACK_THEME_ID),
    [backgroundThemes],
  );

  const isPitchBlackBackground =
    (selectedBackgroundTheme || FALLBACK_BG_THEME).theme_meta_unique_id === PITCH_BLACK_THEME_ID;

  const pageThemeResolved = selectedPageTheme || FALLBACK_PAGE_THEME;
  const pageChrome = useMemo(
    () => getPageThemeChrome(pageThemeResolved),
    [pageThemeResolved],
  );

  const listboxBackgroundTheme = useMemo(() => {
    if (!isPitchBlackBackground) return selectedBackgroundTheme || FALLBACK_BG_THEME;
    if (
      pitchBlackRestoreTheme?.theme_meta_unique_id &&
      pitchBlackRestoreTheme.theme_meta_unique_id !== PITCH_BLACK_THEME_ID
    ) {
      return pitchBlackRestoreTheme;
    }
    return (
      backgroundThemesForDropdown[0] ||
      backgroundThemes.find((t) => t.theme_meta_unique_id !== PITCH_BLACK_THEME_ID) ||
      selectedBackgroundTheme ||
      FALLBACK_BG_THEME
    );
  }, [
    isPitchBlackBackground,
    selectedBackgroundTheme,
    pitchBlackRestoreTheme,
    backgroundThemesForDropdown,
    backgroundThemes,
  ]);

  const readerRootBackgroundStyle = isPitchBlackBackground
    ? {
        backgroundColor: '#000000',
        backgroundImage: 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.35)',
      }
    : {
        backgroundImage: `url(${(selectedBackgroundTheme || FALLBACK_BG_THEME).theme_url || '/images/wood.jpg'})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)',
      };

  const togglePitchBlackBackground = useCallback(() => {
    const dark = backgroundThemes.find((t) => t.theme_meta_unique_id === PITCH_BLACK_THEME_ID);
    if (!dark) return;
    const goingDark =
      (selectedBackgroundTheme || FALLBACK_BG_THEME).theme_meta_unique_id !== PITCH_BLACK_THEME_ID;
    if (goingDark) {
      if ((selectedBackgroundTheme || FALLBACK_BG_THEME).theme_meta_unique_id !== PITCH_BLACK_THEME_ID) {
        setPitchBlackRestoreTheme(selectedBackgroundTheme || FALLBACK_BG_THEME);
      }
      handleBackgroundThemeChange(dark);
      return;
    }
    const restore =
      pitchBlackRestoreTheme &&
      pitchBlackRestoreTheme.theme_meta_unique_id !== PITCH_BLACK_THEME_ID
        ? pitchBlackRestoreTheme
        : backgroundThemes.find((t) => t.theme_meta_unique_id !== PITCH_BLACK_THEME_ID);
    if (restore) handleBackgroundThemeChange(restore);
  }, [
    backgroundThemes,
    selectedBackgroundTheme,
    pitchBlackRestoreTheme,
    handleBackgroundThemeChange,
  ]);

  const leftMainPage = pages[spreadIndex * 2] ?? null;
  const rightMainPage = pages[spreadIndex * 2 + 1] ?? null;
  const stackLeftPage = spreadIndex > 0 ? pages[(spreadIndex - 1) * 2] ?? null : null;
  const nextRightIdx = (spreadIndex + 1) * 2 + 1;
  const stackRightPage =
    spreadIndex < maxSpreadIndex && nextRightIdx < pages.length ? pages[nextRightIdx] : null;

  const animating = enhancedPageFlipEnabled && targetSpreadIndex !== spreadIndex;
  const leftMainZ = animating && targetSpreadIndex < spreadIndex ? 2 : 1;
  const rightMainZ = animating && targetSpreadIndex > spreadIndex ? 3 : 1;

  const flipLeftClass =
    animating && targetSpreadIndex < spreadIndex ? 'flip-prev' : '';
  const flipRightClass =
    animating && targetSpreadIndex > spreadIndex ? 'flip-next' : '';

  const enhancedTextStyle = useMemo(
    () => ({ fontSize: `${READER_FONT_BASE_PX * clampedReaderFontScale}px` }),
    [clampedReaderFontScale],
  );

  const bookShadowSize = Math.round(680 * clampedReaderFontScale);

  const textDir = book?.language?.text_direction || 'ltr';
  const langAttr =
    (typeof book?.language_code === 'string' && book.language_code) ||
    book?.language?.languages_code_unique_id ||
    '';

  const goPrev = () => {
    userNavigatedRef.current = true;
    setTargetSpreadIndex((i) => Math.max(0, i - 1));
  };

  const goNext = () => {
    if (targetSpreadIndex >= maxSpreadIndex) return;
    userNavigatedRef.current = true;
    setTargetSpreadIndex((i) => Math.min(maxSpreadIndex, i + 1));
    recordForwardFlip();
  };

  if (!book && !initialLoading) {
    return (
      <div className="max-w-md mx-auto mt-20 text-center text-slate-700">
        <p className="mb-4">This book could not be loaded. It may be a draft — try signing in.</p>
        <Link to="/" className="text-violet-900 hover:underline">
          Home
        </Link>
      </div>
    );
  }

  return (
    <div
      className="relative flex min-h-screen flex-col bg-gray-200"
      lang={langAttr || undefined}
      dir={textDir === 'rtl' ? 'rtl' : 'ltr'}
      style={readerRootBackgroundStyle}
    >
      {initialLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-violet-800 border-t-transparent" />
        </div>
      )}

      {showPremiumModal && previewTheme && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-center text-xl font-semibold">{previewTheme.theme_name}</h2>
            <img
              src={previewTheme.theme_url}
              alt=""
              className="mb-4 h-56 w-full rounded object-cover"
            />
            <p className="mb-6 text-center text-sm text-gray-600">This is a premium theme.</p>
            <div className="flex justify-center gap-4">
              <a
                href="/pricing"
                className="rounded bg-yellow-500 px-4 py-2 text-white hover:bg-yellow-600"
              >
                Go Premium
              </a>
              <button
                type="button"
                className="text-gray-600 hover:text-gray-800"
                onClick={() => setShowPremiumModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="relative flex w-full shrink-0 flex-1 items-start justify-start overflow-x-auto overflow-y-auto py-2 pb-40 pt-9">
        {book ? (
          <div
            className="relative mx-auto"
            style={{ width: viewportLockWidth, height: enhancedScaledPagePx }}
          >
            <div className="relative flex justify-center">
              <div
                className="absolute rounded-full bg-black opacity-10"
                style={{
                  width: bookShadowSize,
                  height: bookShadowSize,
                  transform: 'translateY(10px)',
                  filter: 'blur(15px)',
                }}
              />

              <AuthoredReaderSpreadLeaf
                key={`ml-${leftMainPage?.authored_page_meta_unique_id ?? spreadIndex}`}
                page={leftMainPage}
                side="left"
                layer="main"
                flipClass={leftMainPage ? flipLeftClass : ''}
                mainZIndex={leftMainZ}
                fixedPanelPx={enhancedScaledPagePx}
                selectedPageTheme={pageThemeResolved}
                innerMainClassName={INNER_MAIN_CLASS}
                innerStackClassName={INNER_STACK_CLASS}
                textStyle={enhancedTextStyle}
                extensions={extensions}
                pageChrome={pageChrome}
                textDir={textDir === 'rtl' ? 'rtl' : 'ltr'}
              />

              <AuthoredReaderSpreadLeaf
                key={`sl-${spreadIndex}`}
                page={stackLeftPage}
                side="left"
                layer="stack"
                fixedPanelPx={enhancedScaledPagePx}
                selectedPageTheme={pageThemeResolved}
                innerMainClassName={INNER_MAIN_CLASS}
                innerStackClassName={INNER_STACK_CLASS}
                textStyle={enhancedTextStyle}
                extensions={extensions}
                pageChrome={pageChrome}
                textDir={textDir === 'rtl' ? 'rtl' : 'ltr'}
              />

              <div
                className="w-[10px] shrink-0"
                style={{
                  height: enhancedScaledPagePx,
                  backgroundColor: pageChrome.bindingColor,
                }}
              />

              <AuthoredReaderSpreadLeaf
                key={`sr-${spreadIndex}`}
                page={stackRightPage}
                side="right"
                layer="stack"
                fixedPanelPx={enhancedScaledPagePx}
                selectedPageTheme={pageThemeResolved}
                innerMainClassName={INNER_MAIN_CLASS}
                innerStackClassName={INNER_STACK_CLASS}
                textStyle={enhancedTextStyle}
                extensions={extensions}
                pageChrome={pageChrome}
                textDir={textDir === 'rtl' ? 'rtl' : 'ltr'}
              />

              <AuthoredReaderSpreadLeaf
                key={`mr-${rightMainPage?.authored_page_meta_unique_id ?? spreadIndex}-r`}
                page={rightMainPage}
                side="right"
                layer="main"
                flipClass={rightMainPage ? flipRightClass : ''}
                mainZIndex={rightMainZ}
                fixedPanelPx={enhancedScaledPagePx}
                selectedPageTheme={pageThemeResolved}
                innerMainClassName={INNER_MAIN_CLASS}
                innerStackClassName={INNER_STACK_CLASS}
                textStyle={enhancedTextStyle}
                extensions={extensions}
                pageChrome={pageChrome}
                textDir={textDir === 'rtl' ? 'rtl' : 'ltr'}
              />

              {readingLampOn ? (
                <div
                  className="pointer-events-none absolute inset-0 z-[15] min-h-full min-w-full rounded-sm"
                  style={{
                    background:
                      'linear-gradient(to bottom, rgba(255, 236, 218, 0.35) 0%, rgba(255, 234, 212, 0.025) 50%, rgba(255, 232, 208, 0.028) 100%), radial-gradient(ellipse 320% 300% at 50% -25%, rgba(255, 218, 175, 0.065) 0%, rgba(255, 228, 200, 0.042) 42%, rgba(255, 233, 210, 0.03) 72%, rgba(255, 235, 215, 0.022) 100%)',
                    boxShadow: 'inset 0 0 200px 80px rgba(255, 228, 200, 0.022)',
                  }}
                  aria-hidden
                />
              ) : null}
            </div>

            <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 space-x-4 md:bottom-3">
              <button
                type="button"
                disabled={targetSpreadIndex <= 0}
                onClick={goPrev}
                className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-violet-800 bg-transparent text-xl text-violet-800 shadow-md transition-all hover:bg-violet-800 hover:text-white disabled:opacity-40"
              >
                &lt;
              </button>
              <button
                type="button"
                disabled={targetSpreadIndex >= maxSpreadIndex}
                onClick={goNext}
                className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-violet-800 bg-transparent text-xl text-violet-800 shadow-md transition-all hover:bg-violet-800 hover:text-white disabled:opacity-40"
              >
                &gt;
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {!initialLoading && book && (
        <div
          className="absolute left-1/2 top-1 z-[25] flex -translate-x-1/2 items-center gap-2"
        >
          {pitchBlackThemeMeta ? (
            <button
              type="button"
              onClick={togglePitchBlackBackground}
              aria-pressed={isPitchBlackBackground}
              title={isPitchBlackBackground ? 'Show background theme' : 'Pitch black background'}
              className={`rounded-full border p-2 shadow-sm backdrop-blur-sm transition-colors ${
                isPitchBlackBackground
                  ? 'border-slate-600/90 bg-slate-800/95 text-amber-200'
                  : 'border-gray-300/90 bg-white/90 text-gray-800 hover:bg-white'
              }`}
            >
              {isPitchBlackBackground ? (
                <SunIcon className="h-3 w-3" aria-hidden />
              ) : (
                <MoonIcon className="h-3 w-3" aria-hidden />
              )}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setReadingLampOn((v) => !v)}
            aria-pressed={readingLampOn}
            title={readingLampOn ? 'Turn off reading lamp' : 'Turn on reading lamp'}
            className={`rounded-full border p-2 shadow-sm backdrop-blur-sm transition-colors ${
              readingLampOn
                ? 'border-amber-400/90 bg-amber-50/95 text-amber-900'
                : 'border-gray-300/90 bg-white/90 text-gray-700 hover:bg-white'
            }`}
          >
            <LightBulbIcon className="h-3 w-3" aria-hidden />
          </button>
        </div>
      )}

      <div className="absolute right-0 top-0 z-30">
        <button
          type="button"
          onClick={() => {
            setShowSettings(true);
            if (!previewMode) {
              posthog.capture('authored_toggle_settings', { book_id: bookId, opened: true });
            }
          }}
          className="p-3 text-3xl text-gray-700 hover:text-violet-900"
          aria-label="Settings"
        >
          ⚙️
        </button>
      </div>

      <div
        className={`fixed right-0 top-0 z-40 flex h-full w-72 transform flex-col gap-6 border-l border-gray-300 bg-white p-6 shadow-lg transition-transform duration-300 ${
          showSettings ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Reader Settings</h2>
          <button
            type="button"
            onClick={() => setShowSettings(false)}
            className="text-xl text-gray-500 hover:text-red-500"
          >
            &times;
          </button>
        </div>

        <div>
          <label htmlFor="authored-page-theme" className="mb-1 block text-sm font-medium text-gray-600">
            Page Theme
          </label>
          <select
            id="authored-page-theme"
            value={(selectedPageTheme || FALLBACK_PAGE_THEME).theme_meta_unique_id}
            onChange={(e) => {
              const pool = pageThemes.length ? pageThemes : [FALLBACK_PAGE_THEME];
              const t = pool.find((x) => x.theme_meta_unique_id === e.target.value);
              if (t) handlePageThemeChange(t);
            }}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
          >
            {(pageThemes.length ? pageThemes : [FALLBACK_PAGE_THEME]).map((t) => (
              <option key={t.theme_meta_unique_id} value={t.theme_meta_unique_id}>
                {t.theme_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="authored-font" className="mb-1 block text-sm font-medium text-gray-600">
            Text size
          </label>
          <input
            id="authored-font"
            type="range"
            min={READER_FONT_SCALE_MIN}
            max={READER_FONT_SCALE_MAX}
            step={0.05}
            value={readerFontScale}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (!Number.isNaN(v)) {
                setReaderFontScale(
                  Math.min(READER_FONT_SCALE_MAX, Math.max(READER_FONT_SCALE_MIN, v)),
                );
              }
            }}
            className="w-full"
          />
          <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
            <span>Smaller</span>
            <button
              type="button"
              className="text-violet-900 underline hover:text-violet-950"
              onClick={() => setReaderFontScale(1)}
            >
              Reset
            </button>
            <span>Larger</span>
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-600">Background Theme</label>
          <ThemeDropdownWithPreview
            themes={backgroundThemesForDropdown.length ? backgroundThemesForDropdown : [FALLBACK_BG_THEME]}
            selectedTheme={listboxBackgroundTheme || FALLBACK_BG_THEME}
            setSelectedTheme={(theme) => handleBackgroundThemeChange(theme)}
            onLockedThemeClick={(theme) => {
              setPreviewTheme(theme);
              setShowPremiumModal(true);
            }}
          />
        </div>

        {(selectedBackgroundTheme || FALLBACK_BG_THEME)?.has_audio === 1 &&
        (selectedBackgroundTheme || FALLBACK_BG_THEME)?.audio_url ? (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-600">Audio</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={audioVolume}
              onChange={(e) => setAudioVolume(parseFloat(e.target.value))}
              className="w-full"
            />
            <button
              type="button"
              onClick={() => setIsMuted((m) => !m)}
              className="mt-2 text-sm text-violet-900 underline"
            >
              {isMuted ? 'Unmute' : 'Mute'}
            </button>
          </div>
        ) : null}

        {loadingTheme ? (
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span>
              Loading <strong>{loadingTheme.theme_name}</strong>…
            </span>
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-violet-800 border-t-transparent" />
          </div>
        ) : null}
      </div>

      {(selectedBackgroundTheme || FALLBACK_BG_THEME)?.has_audio === 1 &&
      (selectedBackgroundTheme || FALLBACK_BG_THEME)?.audio_url ? (
        <audio
          ref={audioRef}
          className="pointer-events-none fixed left-0 top-0 h-px w-px opacity-0"
          aria-hidden
          src={(selectedBackgroundTheme || FALLBACK_BG_THEME).audio_url}
          loop
          autoPlay
          muted={isMuted}
        />
      ) : null}
      {promoElement}
    </div>
  );
}
